import { router, usePage } from '@inertiajs/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import DriveSyncLayout from '../../layouts/DriveSyncLayout';
import UsersHeader from './UsersHeader';

export default function Edit({ user, carriers = [], errors = {} }) {
  const { props } = usePage();

  const [formData, setFormData] = React.useState({
    name: user.name || '',
    email: user.email || '',
    phone: user.phone || '',
    password: '',
    carrier_id: user.carrier_id || '',
    whatsapp: user.roles?.includes('whatsapp') || false
  });
  const [loading, setLoading] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState(errors);

  // Sync server errors with local state when props change
  React.useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
    }
  }, [errors]);

  const getValidErrors = () => {
    return Object.values(formErrors).filter(error => error && error.trim());
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing (both client-side and server errors)
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.phone && !/^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(formData.phone)) {
      newErrors.phone = 'Telefone deve estar no formato (00) 00000-0000';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      router.put(user.update_url, {
        user: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Remove formatting for backend
          password: formData.password || undefined,
          carrier_id: formData.carrier_id || null,
          whatsapp: formData.whatsapp ? '1' : '0'
        }
      }, {
        preserveScroll: true,
        onError: (errors) => {
          setFormErrors(errors);
        },
        onFinish: () => {
          setLoading(false);
        }
      });
    } catch (err) {
      setLoading(false);
    }
  };

  const handleBack = () => {
    router.visit('/users');
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, phone: formatPhone(value) }));
  };

  return (
    <DriveSyncLayout>
      <UsersHeader />

      <Box sx={{
        width: '100%',
        maxWidth: { sm: '100%', md: '800px' },
        mx: 'auto',
        mt: 2
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Editar Usuário
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Edite as informações do usuário {user.name}.
        </Typography>

        {/* Error Alert */}
        {getValidErrors().length > 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" component="div">
              <strong>Foram encontrados {getValidErrors().length} erro(s):</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {getValidErrors().map((error, index) => (
                <Box component="li" key={index}>
                  <Typography variant="body2">{error}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        {/* Form */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <PersonIcon color="primary" />
              <Typography variant="h6">
                Informações do Usuário
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  placeholder="Nome completo do usuário"
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                <TextField
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="usuario@email.com"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                <TextField
                  id="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                />

                <TextField
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  placeholder="Nova Senha"
                  error={!!formErrors.password}
                  helperText={formErrors.password || 'Deixe em branco para manter a senha atual'}
                  disabled={loading}
                  variant="outlined"
                  fullWidth
                />

                {
                  props.current.user.id !== user.id && (
                    <FormControl variant="outlined" fullWidth error={!!formErrors.carrier_id}>
                      <InputLabel id="carrier-label">Transportadora</InputLabel>
                      <Select
                        labelId="carrier-label"
                        value={formData.carrier_id}
                        onChange={handleInputChange('carrier_id')}
                        disabled={loading}
                      >
                        <MenuItem value="">
                          <em>Nenhuma</em>
                        </MenuItem>
                        {carriers.map((carrier) => (
                          <MenuItem key={carrier.id} value={carrier.id}>
                            {carrier.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.carrier_id && (
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          {formErrors.carrier_id}
                        </Typography>
                      )}
                    </FormControl>
                  )
                }


                {/* WhatsApp Role Section */}
                {
                  props.current.roles.includes('god_mode') && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6">
                          Permissões Especiais
                        </Typography>
                      </Box>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.whatsapp}
                            onChange={handleInputChange('whatsapp')}
                            disabled={loading}
                          />
                        }
                        label="Acesso ao WhatsApp IA"
                      />
                    </Box>
                  )
                }
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </Box>
      </Box>
    </DriveSyncLayout>
  );
} 